'use client'

import React, { useEffect, useRef } from 'react'
import { ISection, Language, ITheme } from '@/lib/interfaces/interfaces'
import { GOOGLE_REVIEW_LABELS } from '@/lib/utils/googleReview'
import { getTextInLanguage } from '@/lib/utils/menuText'

interface Props {
  sections: ISection[]
  activeSection: string
  onSectionChange: (sectionId: string) => void
  language: Language
  orderingActive: boolean
  theme: ITheme
}

const SECTION_FALLBACK: Record<string, string> = {
  es: 'Sección',
  en: 'Section',
  fr: 'Section',
  ca: 'Secció',
  de: 'Abschnitt',
  eu: 'Atala'
}

const ModernNavigationCarousel = ({ sections, activeSection, onSectionChange, language, orderingActive, theme }: Props) => {
  const navCarouselRef = useRef<HTMLElement | null>(null)

  // Exclude image sections that have no name (they are decorative, no nav button needed).
  // All other sections are navigable; unnamed ones get a language-aware fallback label.
  const navigableSections = sections.filter(section => {
    if (section.type === 'imageSection') {
      const sectionName = getTextInLanguage(section, 'sectionName', language)
      return !!(sectionName && sectionName.trim() !== '')
    }
    return true
  })

  // Hacer scroll al botón activo cuando cambie la sección
  useEffect(() => {
    const navCarousel = navCarouselRef.current
    const activeButton = navCarousel?.querySelector('.modern-nav-button.active') as HTMLElement | null

    if (activeButton && navCarousel) {
      const buttonRect = activeButton.getBoundingClientRect()
      const carouselRect = navCarousel.getBoundingClientRect()

      // Calcular la posición del botón relativa al contenedor
      const buttonCenter = buttonRect.left + buttonRect.width / 2
      const carouselCenter = carouselRect.left + carouselRect.width / 2

      // Calcular cuánto necesitamos hacer scroll
      const scrollOffset = buttonCenter - carouselCenter

      // Hacer scroll suave para centrar el botón activo
      navCarousel.scrollBy({
        left: scrollOffset,
        behavior: 'smooth'
      })
    }
  }, [activeSection])

  const handleWheelScroll = (event: React.WheelEvent<HTMLElement>) => {
    const navCarousel = navCarouselRef.current

    if (!navCarousel) return

    // En desktop, permitir scroll horizontal usando la rueda vertical
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault()
      navCarousel.scrollBy({
        left: event.deltaY,
        behavior: 'auto'
      })
    }
  }

  return (
    <div
      className={`modern-navigation ${orderingActive ? 'with-ordering' : ''}`}
    >
      <nav className='modern-nav-carousel' ref={navCarouselRef} onWheel={handleWheelScroll}>
        {navigableSections.map(section => {
          const rawName = section.type === 'infoBlock'
            ? 'Info'
            : section.type === 'googleReview'
              ? GOOGLE_REVIEW_LABELS[language]
              : getTextInLanguage(section, 'sectionName', language)
          const sectionName = rawName?.trim() || SECTION_FALLBACK[language] || 'Section'

          const isActive = activeSection === section.sectionId

          return (
            <button
              key={section.sectionId}
              onClick={() => onSectionChange(section.sectionId)}
              className={`modern-nav-button ${isActive ? 'active' : ''}`}
            >
              {sectionName}
            </button>
          )
        })}
      </nav>
    </div>
  )
}

export default ModernNavigationCarousel
